import React from 'react';
import { Navigate, Route } from 'react-router-dom';
import { apiDocsPlugin, ApiExplorerPage } from '@backstage/plugin-api-docs';
import {
  CatalogEntityPage,
  CatalogIndexPage,
  catalogPlugin,
} from '@backstage/plugin-catalog';
import {
  CatalogImportPage,
  catalogImportPlugin,
} from '@backstage/plugin-catalog-import';
import { ScaffolderPage, scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { orgPlugin } from '@backstage/plugin-org';
import { SearchPage } from '@backstage/plugin-search';
import {
  TechDocsIndexPage,
  techdocsPlugin,
  TechDocsReaderPage,
} from '@backstage/plugin-techdocs';
import { TechDocsAddons } from '@backstage/plugin-techdocs-react';
import { ReportIssue } from '@backstage/plugin-techdocs-module-addons-contrib';
import { UserSettingsPage } from '@backstage/plugin-user-settings';
import { apis } from './apis';
import { entityPage } from './components/catalog/EntityPage';
import { searchPage } from './components/search/SearchPage';
import { Root } from './components/Root';

import {
  AlertDisplay,
  OAuthRequestDialog,
  SignInPage,
  SignInProviderConfig,
} from '@backstage/core-components';
import { createApp } from '@backstage/app-defaults';
import { AppRouter, FlatRoutes } from '@backstage/core-app-api';
import { CatalogGraphPage } from '@backstage/plugin-catalog-graph';
import { RequirePermission } from '@backstage/plugin-permission-react';
import { catalogEntityCreatePermission } from '@backstage/plugin-catalog-common/alpha';

// Add Onepoint
import { ScaffolderFieldExtensions } from '@backstage/plugin-scaffolder-react';
import { CBEntityPickerExtension } from '@internal/backstage-plugin-custom-fields';
import {
  MultiRelatedEntityPickerExtension,
  RelatedEntityPickerExtension,
} from '@internal/backstage-plugin-custom-fields/src/plugin';
import { microsoftAuthApiRef } from '@backstage/core-plugin-api';

import { createTheme, genPageTheme, darkTheme, shapes } from '@backstage/theme';
import { ThemeProvider } from '@material-ui/core/styles';
import CssBaseline from '@material-ui/core/CssBaseline';
import LightIcon from '@material-ui/icons/WbSunny';
import { AzureIcon, AwsIcon, AzureDevOpsIcon } from './assets/icons/AzureIcons';
import { FlightLand, LocationOn } from '@material-ui/icons';

const microsoftAuthProvider: SignInProviderConfig = {
  id: 'azure-auth-provider',
  title: 'Microsoft Active Directory',
  message:
    'Sign in to Backstage Application using your Active Directory account.',
  apiRef: microsoftAuthApiRef,
};

const app = createApp({
  apis,
  bindRoutes({ bind }) {
    bind(catalogPlugin.externalRoutes, {
      createComponent: scaffolderPlugin.routes.root,
      viewTechDoc: techdocsPlugin.routes.docRoot,
      createFromTemplate: scaffolderPlugin.routes.selectedTemplate,
    });
    bind(apiDocsPlugin.externalRoutes, {
      registerApi: catalogImportPlugin.routes.importPage,
    });
    bind(scaffolderPlugin.externalRoutes, {
      registerComponent: catalogImportPlugin.routes.importPage,
      viewTechDoc: techdocsPlugin.routes.docRoot,
    });
    bind(orgPlugin.externalRoutes, {
      catalogIndex: catalogPlugin.routes.catalogIndex,
    });
  },
  components: {
    SignInPage: props => {
      return (
        <SignInPage
          {...props}
          provider={microsoftAuthProvider}
          title="Select a sign-in method"
          align="center"
        />
      );
    },
  },
  themes: [
    {
      id: 'cloudbooster-theme',
      title: 'CloudBooster Theme',
      variant: 'dark',
      icon: <LightIcon />,
      Provider: ({ children }) => (
        <ThemeProvider theme={cloudboosterDarkTheme}>
          <CssBaseline>{children}</CssBaseline>
        </ThemeProvider>
      ),
    },
  ],
  icons: {
    'kind:resource': AzureIcon,
    'kind:environment': FlightLand,
    'kind:region': LocationOn,
    azure: AzureIcon,
    'aws-product': AwsIcon,
    azuredevops: AzureDevOpsIcon,
  },
});

const cloudboosterDarkTheme = createTheme({
  palette: {
    ...darkTheme.palette,
    background: {
      default: '#1E2124',
      paper: '#32373c',
    },
    navigation: {
      background: '#32373c',
      indicator: '#1E2124',
      color: '#d5d6db',
      selectedColor: '#ffffff',
      navItem: {
        hoverBackground: '#1E2124',
      },
    },
    primary: {
      main: '#00A5E5',
      dark: '#00A5E5',
    },
  },
  defaultPageTheme: 'home',
  /* below drives the header colors */
  fontFamily:
    '"Poppins",-apple-system,BlinkMacSystemFont,Helvetica,Arial,sans-serif',
  pageTheme: {
    home: genPageTheme({ colors: ['#079bd4', '#0D559D'], shape: shapes.wave }),
    documentation: genPageTheme({
      colors: ['#B8006D', '#5C0036'],
      shape: shapes.wave2,
    }),
    environment: genPageTheme({
      colors: ['#50BF9E', '#246351'],
      shape: shapes.wave,
    }),
    region: genPageTheme({
      colors: ['#50BF9E', '#246351'],
      shape: shapes.wave,
    }),
    'aws-product': genPageTheme({
      colors: ['#ffab00', '#cd5900'],
      shape: shapes.wave,
    }),
    'paas-product-extended': genPageTheme({
      colors: ['#079bd4', '#A02B93'],
      shape: shapes.wave,
    }),
    tool: genPageTheme({ colors: ['#079bd4', '#0D559D'], shape: shapes.round }),
    service: genPageTheme({
      colors: ['#079bd4', '#0D559D'],
      shape: shapes.wave,
    }),
    website: genPageTheme({
      colors: ['#079bd4', '#0D559D'],
      shape: shapes.wave,
    }),
    library: genPageTheme({
      colors: ['#079bd4', '#0D559D'],
      shape: shapes.wave,
    }),
    other: genPageTheme({ colors: ['#079bd4', '#0D559D'], shape: shapes.wave }),
    app: genPageTheme({ colors: ['#079bd4', '#0D559D'], shape: shapes.wave }),
    apis: genPageTheme({ colors: ['#079bd4', '#0D559D'], shape: shapes.wave }),
  },
});

const routes = (
  <FlatRoutes>
    <Route path="/" element={<Navigate to="catalog" />} />
    <Route path="/catalog" element={<CatalogIndexPage />} />
    <Route
      path="/catalog/:namespace/:kind/:name"
      element={<CatalogEntityPage />}
    >
      {entityPage}
    </Route>
    <Route path="/docs" element={<TechDocsIndexPage />} />
    <Route
      path="/docs/:namespace/:kind/:name/*"
      element={<TechDocsReaderPage />}
    >
      <TechDocsAddons>
        <ReportIssue />
      </TechDocsAddons>
    </Route>

    <Route path="/create" element={<ScaffolderPage />}>
      <ScaffolderFieldExtensions>
        <CBEntityPickerExtension />
        <MultiRelatedEntityPickerExtension />
        <RelatedEntityPickerExtension />
      </ScaffolderFieldExtensions>
    </Route>

    <Route path="/api-docs" element={<ApiExplorerPage />} />
    <Route
      path="/catalog-import"
      element={
        <RequirePermission permission={catalogEntityCreatePermission}>
          <CatalogImportPage />
        </RequirePermission>
      }
    />
    <Route path="/search" element={<SearchPage />}>
      {searchPage}
    </Route>
    <Route path="/settings" element={<UserSettingsPage />} />
    <Route path="/catalog-graph" element={<CatalogGraphPage />} />
  </FlatRoutes>
);

export default app.createRoot(
  <>
    <AlertDisplay />
    <OAuthRequestDialog />
    <AppRouter>
      <Root>{routes}</Root>
    </AppRouter>
  </>,
);
